import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  Modal,
  GET,
  POST,
  PUT,
  DELETE,
  t,
  // นำเข้าฟังก์ชัน t,
  Select,
} from "../../components/CustomComponent.js";
import { can } from "../../utils/permission.js";
import { isExportKey } from "../../utils/portalAccess.js";
import { formatNumber } from "../../utils/formatting";

// รวมแถวสิทธิ์ของทุกพอร์ทัลจากเมนูใน global.js
// permission key เดียวกันถูกใช้ซ้ำได้ทั้งในเมนูเดียวกัน (เช่น "ใบงาน" กับ "สรุปรายการในใบสั่งงาน" ใช้ wo.read)
// และข้ามพอร์ทัล (wo/asset อยู่ทั้งเมนูแอดมินและเมนูลูกค้า)
// ถ้าแยกเป็นคนละแถว การติ๊กออกแถวหนึ่งจะถูกแถวอื่นที่ยังติ๊กอยู่ใส่ค่ากลับตอนรวม payload
// ผลคือกดบันทึกแล้วสิทธิ์ไม่เปลี่ยน - จึงยุบเหลือแถวเดียวต่อหนึ่ง permission และ "ใช้ออบเจ็กต์เดียวกัน"
// ข้ามพอร์ทัล เพื่อให้ติ๊กที่แท็บไหนก็มีผลตรงกันทุกแท็บ
// entry ที่ permission ไม่ลงท้าย ".read" (เช่น pool_care_report.export, contract-checkin.create) = สิทธิ์เดี่ยว
// key จริงคือค่านั้นทั้งก้อน ไม่ต่อ .read/.create/... เพิ่ม - แถวนี้ใช้ช่อง "อ่าน" เป็นตัวติ๊กเปิด/ปิดสิทธิ์
const isSinglePermission = (key) => !String(key).endsWith(".read");
const permissionBase = (key) => (isSinglePermission(key) ? key : String(key).replaceAll(".read", ""));

// entry ใน global.js ใส่ actions: ["read"] ได้ เพื่อบอกว่าสิทธิ์นี้มีเฉพาะ action ที่ระบุ (ไม่มี key อื่นในตาราง permissions)
// ไม่ใส่ actions = มีครบ read/create/update/delete เหมือนเดิม
const hasAction = (item, action) =>
  !item.single && (!item.actions || item.actions.includes(action));


const ACTION_KEYS = ["read", "create", "update", "delete"];
const ACTION_LABEL = { read: "view", create: "create", update: "edit", delete: "delete_action" };

// ช่องที่โมดูลนี้มีจริงในแท็บนั้น (ช่องที่ไม่มี แสดง "–")
// แท็บที่ประเภทผู้ใช้ "ดูอย่างเดียว" เหลือแค่ช่อง "ดู"
const maskOf = (item, viewOnly) => {
  if (item.single || viewOnly) return ["read"];
  return ["read", ...["create", "update", "delete"].filter((a) => hasAction(item, a))];
};

// ระดับสิทธิ์ -> ค่าติ๊กของแต่ละช่องที่มี
const flagsForLevel = (mask, level) => {
  const f = { read: false, create: false, update: false, delete: false };
  if (level === "view") f.read = mask.includes("read");
  if (level === "edit") ["read", "create", "update"].forEach((k) => { if (mask.includes(k)) f[k] = true; });
  if (level === "full") mask.forEach((k) => { f[k] = true; });
  return f;
};

// ค่าติ๊กปัจจุบัน -> ระดับสิทธิ์ (ไม่ตรงระดับไหนเลย = custom)
const levelOf = (mask, item) => {
  const on = mask.filter((k) => item[k]);
  if (!on.length) return "none";
  if (on.length === mask.length) return "full";
  if (on.length === 1 && item.read) return "view";
  const editKeys = ["read", "create", "update"].filter((k) => mask.includes(k));
  if (editKeys.every((k) => item[k]) && !item.delete) return "edit";
  return "custom";
};

// แก้ค่าในออบเจ็กต์เดิม (แถวเดียวกันถูกใช้ร่วมกันข้ามแท็บ)
const applyFlags = (item, flags) => {
  ACTION_KEYS.forEach((k) => { item[k] = !!flags[k]; });
};

// ติ๊ก สร้าง/แก้ไข/ลบ -> ติ๊ก "ดู" ให้อัตโนมัติ · เอา "ดู" ออก -> สิทธิ์อื่นของโมดูลนั้นหายตาม
const setFlag = (item, mask, key, value) => {
  if (!mask.includes(key)) return;
  item[key] = value;
  if (key !== "read" && value && mask.includes("read")) item.read = true;
  if (key === "read" && !value) {
    item.create = false;
    item.update = false;
    item.delete = false;
  }
};

// 3 สถานะ: "" ว่าง / "some" บางส่วน / "all" ทั้งหมด
const triState = (pairs) => {
  if (!pairs.length) return "";
  const on = pairs.filter(([item, k]) => item[k]).length;
  return on === 0 ? "" : on === pairs.length ? "all" : "some";
};

const TriCheckbox = ({ state, ...rest }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input pointer"
      checked={state === "all"}
      {...rest}
    />
  );
};

const allRowsOf = (groups) => {
  const byPermission = {};
  Object.values(groups || {}).forEach((rows) => rows.forEach((row) => { byPermission[row.permission] = row; }));
  return Object.values(byPermission);
};

const DIRTY_BG = "#FFF1C2";

const buildPermissionGroups = (rolePermissions = []) => {
  const rowByPermission = {};

  // ชื่อโมดูลเก็บแยกตามพอร์ทัล แต่ละแท็บจะได้โชว์เฉพาะเมนูของตัวเอง
  // (พอร์ทัลลูกค้าใช้ wo.read ถึง 7 เมนู ถ้าเอาชื่อทุกพอร์ทัลมาต่อกันจะยาวจนอ่านไม่รู้เรื่อง)
  // groupByPortal = หัวข้อเมนู (entry { title }) ที่โมดูลนั้นอยู่ใต้ ใช้จัดกลุ่มตารางตามเมนูซ้าย
  const buildRows = (menu, group) => {
    const rows = [];
    let currentTitle = "";

    for (const item of menu || []) {
      if (item.title && !item.name) {
        currentTitle = item.title;
        continue;
      }
      if (!item.name) continue;
      // เมนูที่คุมด้วยประเภทผู้ใช้ (ตั้งค่าระบบ = super_user / it_admin) ไม่ใช่สิทธิ์ของบทบาท ไม่แสดงในหน้านี้
      if (item.userTypes) continue;
      const permission = permissionBase(item.permission);
      const single = isSinglePermission(item.permission);
      let row = rowByPermission[permission];

      if (!row) {
        row = {
          module: item.name,
          modulesByGroup: {},
          groupByPortal: {},
          icon: item.icon,
          permission,
          single,
          actions: item.actions,
          read: rolePermissions.includes(single ? permission : permission + ".read"),
          create: hasAction({ single, actions: item.actions }, "create") && rolePermissions.includes(permission + ".create"),
          update: hasAction({ single, actions: item.actions }, "update") && rolePermissions.includes(permission + ".update"),
          delete: hasAction({ single, actions: item.actions }, "delete") && rolePermissions.includes(permission + ".delete"),
        };
        rowByPermission[permission] = row;
      }

      if (!row.groupByPortal[group]) row.groupByPortal[group] = currentTitle;
      const names = (row.modulesByGroup[group] ||= []);
      if (!names.includes(item.name)) names.push(item.name);
      if (!rows.includes(row)) rows.push(row);
    }

    return rows;
  };

  return {
    admin: buildRows(global.menu_admin, "admin"),
    customer: buildRows(global.menu_customer, "customer"),
    mobile: buildRows(global.menu_mobile, "mobile"),
    warehouse: buildRows(global.menu_warehouse, "warehouse"),
  };
};

// สิทธิ์ที่ไม่มีแถวไหนในหน้านี้คุมอยู่ (เช่น permissions.*, dashboard.*, po.* ที่ไม่ได้อยู่ในเมนู)
// ต้องส่งกลับไปเหมือนเดิมตอนบันทึก ไม่งั้น backend จะลบทิ้งเพราะ payload คือสิทธิ์ชุดใหม่ทั้งชุด
const uncontrolledPermissions = (rolePermissions = []) => {
  const controlled = new Set(
    [
      ...(global.menu_admin || []),
      ...(global.menu_customer || []),
      ...(global.menu_warehouse || []),
      ...(global.menu_mobile || []),
    ]
      // รายการที่คุมด้วยประเภทผู้ใช้ไม่มีแถวในหน้านี้ -> สิทธิ์เดิมของ role (ถ้ามี) ถือเป็น uncontrolled คงไว้ตามเดิมตอนบันทึก
      .filter((e) => e.name && !e.userTypes)
      .map((e) => permissionBase(e.permission)),
  );

  return rolePermissions.filter(
    (key) =>
      !controlled.has(key) &&
      !controlled.has(String(key).replace(/\.(read|create|update|delete)$/, "")),
  );
};

const TABS = [
  { id: "admin", label: "admin_portal", icon: "" },
  { id: "customer", label: "customer_portal", icon: "" },
  { id: "mobile", label: "mobile_app", icon: "" },
  { id: "warehouse", label: "warehouse_inventory", icon: "" },
];

export default class RolesPermissions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modal_create: false,
      modal_leave: false,
      new_role_name: "",
      new_role_error: "",

      active_tab: "admin",
      selected_company: "SNAPA Service Co. (Service Provider)",

      data: [],
      active_role_id: 1,

      // ค้นหา: บทบาท (ฝั่งรายการ) / โมดูล (ตารางสิทธิ์)
      role_search: "",
      module_search: "",

      // เมนู ⋮ ของรายการบทบาท
      menu_uid: null,

      // แก้ชื่อ inline
      renaming: false,
      rename_value: "",
      rename_error: "",

      toast_message: "",

      permissions: buildPermissionGroups(),

      uid: "",
      name: "",
      permission: [],
      // ประเภทผู้ใช้ที่บทบาทนี้ใช้กับ (key ใน master, ว่าง = ทุกประเภท) - ถูกเซ็ตจาก ...role ตอนเลือกบทบาท
      user_type: "",
      data_user_types: [],
    };
    // action ที่รอผู้ใช้ตัดสินใจในกล่องเตือน "มีการแก้ไขที่ยังไม่บันทึก"
    this.pendingAction = null;
  }

  // ---- ประเภทผู้ใช้ของบทบาท (master user_types) ----
  PORTAL_FIELD = { admin: "admin_portal", warehouse: "warehouse_portal", customer: "customer_portal", mobile: "mobile_portal" };

  selectedUserType = () => (this.state.data_user_types || []).find((row) => row.key === this.state.user_type) || null;

  // แท็บ (พอร์ทัล) ที่ประเภทนี้ "ดูอย่างเดียว" (view / ve) -> ติ๊กได้แค่ "ดู" ช่องอื่นปิดและไม่ถูกบันทึก
  isViewOnlyTab = (tab) => ["view", "ve"].includes(this.selectedUserType()?.[this.PORTAL_FIELD[tab]]);

  // โหมด ve = ดู + export: สิทธิ์เดี่ยว .export (เช่น attendance.export) ยังติ๊กได้ (ตกลง 2026-10-18)
  isViewExportTab = (tab) => this.selectedUserType()?.[this.PORTAL_FIELD[tab]] === "ve";

  // แถวสิทธิ์เดี่ยว (key ไม่ลงท้าย .read เช่น .export / contract-checkin.create) ถูกล็อกบนแท็บดูอย่างเดียว
  // ยกเว้น .export บนแท็บ ve - ตรงกับ effectivePermissions / checkPermission ที่ตัด key เหล่านี้ทิ้งตอนใช้งาน
  isRowLocked = (tab, item) =>
    !!item.single && this.isViewOnlyTab(tab) && !(this.isViewExportTab(tab) && isExportKey(item.permission));

  userTypeOptions = () =>
    (this.state.data_user_types || [])
      .filter((row) => row.is_active !== false)
      .map((row) => ({ value: row.key, label: `${(localStorage.getItem("lang") === "EN" ? row.name_en : row.name_th) || row.key} (${row.code})` }));

  renderViewOnlyHint = (tab) =>
    this.isViewOnlyTab(tab) ? (
      <div className="text-warning text-12 mb-2 d-flex align-items-center">
        <span className="icon me-2">{""}</span> {t(this.isViewExportTab(tab) ? "role_view_export_hint" : "role_view_only_hint")}
      </div>
    ) : null;

  async componentDidMount() {
    loading(false);
    this.Get();
    document.addEventListener("click", this.closeMenuOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.closeMenuOutside);
    clearTimeout(this.toastTimer);
  }

  closeMenuOutside = (e) => {
    if (this.state.menu_uid && !e.target.closest?.("[data-role-menu]")) this.setState({ menu_uid: null });
  };

  showToast = (message) => {
    clearTimeout(this.toastTimer);
    this.setState({ toast_message: message });
    this.toastTimer = setTimeout(() => this.setState({ toast_message: "" }), 2800);
  };

  Get = async () => {
    let data, count;
    loading(true);

    let result = await GET("admin/roles", {
      page_size: 100,
      page: 0,
    });

    if (result?.status) {
      data = result.data || [];
      count = result.pagination?.count || 0;
    }

    const result_types = await GET("admin/user-types", null);
    this.setState({ data: data || [], count: count || 0, data_user_types: result_types?.status ? result_types.data || [] : [] });
    loading(false);
    // คืนรายการที่โหลดมา ให้ผู้เรียกใช้ต่อได้ทันที (setState ยังไม่ถูกนำไปใช้หลัง await)
    return data || [];
  };

  // ---- บทบาทที่เลือก / ส่วนที่ยังไม่บันทึก ----
  getActiveRole = () => (this.state.data || []).find((r) => r.uid === this.state.active_role_id);

  // ค่าสิทธิ์ที่บันทึกอยู่ในระบบ (ใช้เทียบหา "ช่องที่แก้ไขแล้วยังไม่บันทึก")
  getBaseline = () => {
    const perm = this.getActiveRole()?.permission || [];
    if (!this._base || this._baseSource !== perm) {
      const map = {};
      allRowsOf(buildPermissionGroups(perm)).forEach((row) => { map[row.permission] = row; });
      this._base = map;
      this._baseSource = perm;
    }
    return this._base;
  };

  isCellChanged = (row, key) => !!row[key] !== !!this.getBaseline()[row.permission]?.[key];

  getDirtyRows = () =>
    allRowsOf(this.state.permissions).filter((row) => ACTION_KEYS.some((k) => this.isCellChanged(row, k)));

  isTypeChanged = () => {
    const role = this.getActiveRole();
    return !!role && (this.state.user_type || "") !== (role.user_type || "");
  };

  isDirty = () => this.getDirtyRows().length > 0 || this.isTypeChanged();

  // ชื่อโมดูลที่แสดงในตาราง (แถวเดียวอาจมีหลายเมนู)
  rowName = (row, tab) => (row.modulesByGroup?.[tab] || [row.module]).map((m) => t(m)).join(" / ");

  selectRole = (role, extra = {}) => {
    this.setState({
      active_role_id: role.uid,
      ...role,
      permissions: buildPermissionGroups(role.permission),
      menu_uid: null,
      renaming: false,
      rename_error: "",
      ...extra,
    });
  };

  // สลับบทบาท/เพิ่มบทบาท ก่อนบันทึก -> กล่องเตือน
  guard = (action) => {
    if (this.isDirty()) {
      this.pendingAction = action;
      this.setState({ modal_leave: true, menu_uid: null });
    } else {
      action();
    }
  };

  closeLeave = () => {
    this.pendingAction = null;
    this.setState({ modal_leave: false });
  };

  leaveDiscard = () => {
    const action = this.pendingAction;
    this.pendingAction = null;
    this.setState({ modal_leave: false });
    // action ถัดไป (เลือกบทบาทอื่น/เปิดฟอร์มเพิ่ม) จะรีเซ็ตสิทธิ์ที่แก้ค้างเอง
    if (action) action();
  };

  leaveSave = async () => {
    const action = this.pendingAction;
    this.pendingAction = null;
    this.setState({ modal_leave: false });
    const ok = await this.Update(true);
    if (ok && action) action();
  };

  revertChanges = () => {
    const role = this.getActiveRole();
    if (!role) return;
    this.setState({ permissions: buildPermissionGroups(role.permission), user_type: role.user_type || "" });
  };

  // ---- เพิ่ม / คัดลอก / แก้ชื่อ / ลบ ----
  validateRoleName = (value, exceptUid) => {
    const v = String(value || "").trim();
    if (!v) return t("verify_role_name");
    const key = v.toLowerCase();
    const taken = (this.state.data || []).some((r) => r.uid !== exceptUid && String(r.name).trim().toLowerCase() === key);
    return taken ? t("rp_name_duplicate") : "";
  };

  openCreate = () => {
    this.setState({ modal_create: true, new_role_name: "", new_role_error: "", new_role_type: "" });
  };

  Create = async () => {
    const { new_role_name } = this.state;
    const error = this.validateRoleName(new_role_name, null);
    if (error) {
      this.setState({ new_role_error: error });
      return;
    }

    if (
      await alert(
        "",
        "info",
        t("confirm"),
        t("confirm_submission"),
        t("confirm"),
        t("cancel"),
      )
    ) {
      loading(true);
      let body = {
        name: new_role_name.trim(),
        permission: ["users.read"],
        user_type: this.state.new_role_type || null,
      };

      let result = await POST("admin/roles", body);

      if (result?.status === true) {
        this.setState({ modal_create: false });
        const list = await this.Get();
        const created = list.find((r) => r.name === body.name);
        if (created) this.selectRole(created);
        this.showToast(t("rp_toast_created"));
      } else {
        alert(
          "",
          "warning",
          t("warning"),
          result?.message || "An error occurred.",
        );
      }
      loading(false);
    }
  };

  // คัดลอก: สร้างบทบาทใหม่ด้วย POST เดิม (roles.create) พร้อมสิทธิ์ทุกแท็บ + ประเภทผู้ใช้ (ไม่คัดลอกผู้ใช้)
  CopyRole = async (role) => {
    const base = `${role.name} (${t("rp_copy_suffix")})`;
    let name = base;
    let i = 2;
    const existing = new Set((this.state.data || []).map((r) => String(r.name).trim().toLowerCase()));
    while (existing.has(name.toLowerCase())) name = `${role.name} (${t("rp_copy_suffix")} ${i++})`;

    loading(true);
    const result = await POST("admin/roles", {
      name,
      permission: role.permission?.length ? role.permission : ["users.read"],
      user_type: role.user_type || null,
    });

    if (result?.status === true) {
      const list = await this.Get();
      const created = list.find((r) => r.name === name);
      if (created) {
        this.selectRole(created, { renaming: true, rename_value: created.name, rename_error: "" });
        this.showToast(t("rp_toast_copied"));
      }
    } else {
      alert("", "warning", t("warning"), result?.message || "An error occurred.");
    }
    loading(false);
  };

  startRename = () => {
    const role = this.getActiveRole();
    if (!role || !can("roles.update")) return;
    this.setState({ renaming: true, rename_value: role.name, rename_error: "", menu_uid: null });
  };

  cancelRename = () => this.setState({ renaming: false, rename_error: "" });

  onRenameChange = (e) => {
    const value = e.target.value;
    this.setState({ rename_value: value, rename_error: this.validateRoleName(value, this.state.active_role_id) });
  };

  CommitRename = async () => {
    const role = this.getActiveRole();
    if (!role) return;
    const value = this.state.rename_value.trim();
    const error = this.validateRoleName(value, role.uid);
    if (error) {
      this.setState({ rename_error: error });
      return;
    }
    if (value === role.name) {
      this.cancelRename();
      return;
    }

    loading(true);
    // ส่งสิทธิ์/ประเภทผู้ใช้ที่บันทึกอยู่ในระบบ (ไม่ใช่ค่าที่แก้ค้างในตาราง) เพื่อเปลี่ยนเฉพาะชื่อ
    const result = await PUT("admin/roles/" + role.uid, {
      uid: role.uid,
      name: value,
      permission: role.permission?.length ? role.permission : ["users.read"],
      user_type: role.user_type || null,
    });

    if (result?.status === true) {
      await this.Get();
      this.setState({ renaming: false, rename_error: "", name: value });
      this.showToast(t("rp_toast_renamed").replace("{name}", value));
    } else {
      this.setState({ rename_error: result?.message || "An error occurred." });
    }
    loading(false);
  };

  // บันทึกสิทธิ์ของบทบาทที่เลือก - คืน true เมื่อสำเร็จ
  Update = async (skipConfirm = false) => {
    const { uid, name, permissions, user_type } = this.state;

    if (!name.trim()) {
      alert("", "warning", t("warning"), t("verify_role_name"));
      return false;
    }

    if (
      !skipConfirm &&
      !(await alert(
        "",
        "info",
        t("confirm"),
        t("confirm_submission"),
        t("confirm"),
        t("cancel"),
      ))
    ) {
      return false;
    }

    // เริ่มจากสิทธิ์ที่หน้านี้ไม่มีแถวคุม เพื่อไม่ให้ถูกลบทิ้งตอนบันทึก
    let permissionSet = new Set(uncontrolledPermissions(this.state.permission));

    // พอร์ทัลที่ประเภทผู้ใช้ของบทบาทนี้ "ดูอย่างเดียว" บันทึกได้แค่ .read (backend ปฏิเสธ key อื่นตอนใช้งานอยู่แล้ว)
    const addPermission = (item, tab) => {
      const viewOnly = this.isViewOnlyTab(tab);
      if (item.single) {
        if (item.read && !this.isRowLocked(tab, item)) permissionSet.add(item.permission);
        return;
      }
      if (item.read) permissionSet.add(item.permission + ".read");
      if (viewOnly) return;
      if (item.create && hasAction(item, "create")) permissionSet.add(item.permission + ".create");
      if (item.update && hasAction(item, "update")) permissionSet.add(item.permission + ".update");
      if (item.delete && hasAction(item, "delete")) permissionSet.add(item.permission + ".delete");
    };

    for (let item of permissions.admin || []) addPermission(item, "admin");
    for (let item of permissions.customer || []) addPermission(item, "customer");
    for (let item of permissions.warehouse || []) addPermission(item, "warehouse");
    for (let item of permissions.mobile || []) addPermission(item, "mobile");

    let permission = Array.from(permissionSet);

    loading(true);
    let body = {
      uid,
      name,
      permission: permission.length === 0 ? ["users.read"] : permission,
      user_type: user_type || null,
    };

    let result = !uid
      ? await POST("admin/roles", body)
      : await PUT("admin/roles/" + uid, body);

    let ok = false;
    if (result?.status === true) {
      ok = true;
      // ต้องดึงรายการบทบาทใหม่ ไม่งั้นแถวสิทธิ์ยังอ่านจาก data ชุดเดิมในหน่วยความจำ
      // กดบันทึกแล้วกลับมาดูจะเห็นค่าเก่า ทั้งที่ฝั่งเซิร์ฟเวอร์บันทึกไปแล้ว
      const list = await this.Get();
      const fresh = list.find((r) => r.uid === uid);
      if (fresh) this.selectRole(fresh);
      this.showToast(t("rp_toast_saved").replace("{name}", name));
    } else {
      alert(
        "",
        "warning",
        t("warning"),
        result?.message || "An error occurred.",
      );
    }
    loading(false);
    return ok;
  };

  Delete = async (id) => {
    const role = (this.state.data || []).find((r) => r.uid === id);
    if (role && role.user_count > 0) {
      alert("", "warning", t("warning"), t("cannot_delete_role_in_use"));
      return;
    }
    if (
      await alert(
        "",
        "warning",
        t("delete_item"),
        t("delete_desc"),
        t("confirm"),
        t("cancel"),
      )
    ) {
      loading(true);
      let result = await DELETE("admin/roles/" + id, null);
      if (result?.status === true) {
        if (this.state.active_role_id === id) {
          this.setState({ active_role_id: null, renaming: false, permissions: buildPermissionGroups() });
        }
        await this.Get();
        this.showToast(t("rp_toast_deleted"));
      } else if (result?.message === "Cannot delete or deactivate this item because it is in use") {
        alert('', 'warning', t('warning'), t('cannot_delete_role_in_use'));
      }
      loading(false);
    }
  };

  // ---- ตารางสิทธิ์ ----
  commitPermissions = () => this.setState({ permissions: { ...this.state.permissions } });

  // แถวที่แสดงอยู่ตามคำค้นโมดูล
  visibleRows = (tab) => {
    const q = this.state.module_search.trim().toLowerCase();
    return (this.state.permissions[tab] || []).filter((row) => !q || this.rowName(row, tab).toLowerCase().includes(q));
  };

  // เลือกทั้งหมด / ล้างทั้งหมด -> เฉพาะโมดูลที่แสดงอยู่ตามคำค้น
  bulkLevel = (tab, level) => {
    const viewOnly = this.isViewOnlyTab(tab);
    this.visibleRows(tab)
      .filter((row) => !this.isRowLocked(tab, row))
      .forEach((row) => applyFlags(row, flagsForLevel(maskOf(row, viewOnly), level)));
    this.commitPermissions();
  };

  renderPermissions = (tab) => {
    const viewOnly = this.isViewOnlyTab(tab);
    const { module_search } = this.state;
    const visible = this.visibleRows(tab);
    const editable = visible.filter((row) => !this.isRowLocked(tab, row));
    const maskFor = (row) => maskOf(row, viewOnly);

    const groups = [];
    visible.forEach((row) => {
      const title = row.groupByPortal?.[tab] || "";
      let group = groups.find((g) => g.title === title);
      if (!group) {
        group = { title, rows: [] };
        groups.push(group);
      }
      group.rows.push(row);
    });

    const pairsOf = (rows, keys) =>
      rows.flatMap((row) => maskFor(row).filter((k) => !keys || keys.includes(k)).map((k) => [row, k]));

    const toggleRows = (rows) => {
      const all = triState(pairsOf(rows)) === "all";
      rows.forEach((row) => applyFlags(row, flagsForLevel(maskFor(row), all ? "none" : "full")));
      this.commitPermissions();
    };

    const toggleColumn = (key) => {
      const pairs = pairsOf(editable, [key]);
      const all = triState(pairs) === "all";
      pairs.forEach(([row]) => setFlag(row, maskFor(row), key, !all));
      this.commitPermissions();
    };

    const cellStyle = (row, key) => (this.isCellChanged(row, key) ? { backgroundColor: DIRTY_BG } : undefined);

    return (
      <div className="table-responsive mt-3">
        {this.renderViewOnlyHint(tab)}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <div className="d-flex align-items-center border rounded-pill px-3 bg-white" style={{ minWidth: 240 }}>
            <span className="icon text-secondary me-2 text-12">{""}</span>
            <input
              type="text"
              className="form-control border-0 shadow-none px-0 py-1 text-13"
              placeholder={t("rp_module_search")}
              aria-label={t("rp_module_search")}
              value={module_search}
              onChange={(e) => this.setState({ module_search: e.target.value })}
            />
          </div>
          <div className="text-12 fw-bold pointer">
            <span
              className={visible.length ? "text-primary" : "text-secondary"}
              onClick={() => visible.length && this.bulkLevel(tab, "full")}
            >
              {t("select_all")}
            </span>{" "}
            <span className="text-secondary fw-normal mx-2">|</span>{" "}
            <span
              className="text-secondary"
              onClick={() => visible.length && this.bulkLevel(tab, "none")}
            >
              {t("clear_all")}
            </span>
          </div>
        </div>
        <table className="table table-hover align-middle">
          <thead>
            <tr className="text-dark fw-bold text-12 border-bottom">
              <th className="py-2 border-0">{t("module")}</th>
              {ACTION_KEYS.map((k) => {
                const pairs = pairsOf(editable, [k]);
                return (
                  <th key={k} className="py-2 border-0 text-center">
                    <label className="d-flex flex-column align-items-center gap-1 pointer mb-0">
                      <TriCheckbox
                        state={triState(pairs)}
                        disabled={!pairs.length}
                        aria-label={t(ACTION_LABEL[k])}
                        onChange={() => toggleColumn(k)}
                      />
                      {t(ACTION_LABEL[k])}
                    </label>
                  </th>
                );
              })}
              <th className="py-2 border-0 text-center">{t("rp_level")}</th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan={6} className="text-center text-secondary py-5">
                  {t("rp_module_not_found")} {module_search.trim() ? `“${module_search.trim()}”` : ""}
                  <div>
                    <button
                      className="btn btn-white border btn-sm mt-2"
                      onClick={() => this.setState({ module_search: "" })}
                    >
                      {t("rp_clear_search")}
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {groups.map((group) => {
              const groupEditable = group.rows.filter((row) => !this.isRowLocked(tab, row));
              return (
                <React.Fragment key={group.title || "_"}>
                  <tr className="border-bottom">
                    <td colSpan={6} className="pt-3 pb-2 bg-light">
                      <label className="d-flex align-items-center gap-2 pointer mb-0 fw-bold text-secondary text-13">
                        <TriCheckbox
                          state={triState(pairsOf(groupEditable))}
                          disabled={!groupEditable.length}
                          aria-label={group.title ? t(group.title) : t(TABS.find((x) => x.id === tab)?.label)}
                          onChange={() => toggleRows(groupEditable)}
                        />
                        {group.title ? t(group.title) : t(TABS.find((x) => x.id === tab)?.label)}
                      </label>
                    </td>
                  </tr>
                  {group.rows.map((item) => {
                    const mask = maskFor(item);
                    const locked = this.isRowLocked(tab, item);
                    const level = locked ? "none" : levelOf(mask, item);
                    const levelOptions =
                      mask.length === 1
                        ? [["none", t("rp_level_none")], ["full", t(item.single ? "rp_level_granted" : "rp_level_view")]]
                        : [
                            ["none", t("rp_level_none")],
                            ["view", t("rp_level_view")],
                            ...(mask.includes("create") || mask.includes("update") ? [["edit", t("rp_level_edit")]] : []),
                            ["full", t("rp_level_full")],
                          ];
                    return (
                      <tr key={item.permission} className="border-bottom">
                        <td className="py-2 ps-4">
                          <label className="d-flex align-items-center gap-2 pointer mb-0">
                            <TriCheckbox
                              state={locked ? "" : triState(pairsOf([item]))}
                              disabled={locked}
                              aria-label={this.rowName(item, tab)}
                              onChange={() => toggleRows([item])}
                            />
                            <span className="icon text-primary text-14">{item.icon}</span>
                            <span className="text-dark text-14 fw-bold">{this.rowName(item, tab)}</span>
                          </label>
                        </td>
                        {ACTION_KEYS.map((k) => (
                          <td key={k} className="text-center" style={mask.includes(k) ? cellStyle(item, k) : undefined}>
                            {mask.includes(k) ? (
                              <input
                                type="checkbox"
                                className="form-check-input pointer"
                                checked={!!item[k] && !locked}
                                disabled={locked}
                                aria-label={`${t(ACTION_LABEL[k])} ${this.rowName(item, tab)}`}
                                onChange={(e) => {
                                  setFlag(item, mask, k, e.target.checked);
                                  this.commitPermissions();
                                }}
                              />
                            ) : (
                              <span className="text-secondary" title={t("rp_not_applicable")}>–</span>
                            )}
                          </td>
                        ))}
                        <td className="text-center">
                          <select
                            className="form-select form-select-sm"
                            style={{ minWidth: 140 }}
                            value={level}
                            disabled={locked}
                            aria-label={`${t("rp_level")} ${this.rowName(item, tab)}`}
                            onChange={(e) => {
                              applyFlags(item, flagsForLevel(mask, e.target.value));
                              this.commitPermissions();
                            }}
                          >
                            {levelOptions.map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                            {level === "custom" && <option value="custom" disabled>{t("rp_level_custom")}</option>}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  renderRoleItem = (role) => {
    const { active_role_id, menu_uid } = this.state;
    const active = active_role_id === role.uid;
    const code = role.user_type_info?.code || t("role_user_type_all");
    const showMenu = can("roles.update") || can("roles.create") || can("roles.delete");
    return (
      <div
        key={role.uid}
        className={`position-relative d-flex align-items-center rounded-3 border ${active ? "bg-primary-light border-primary" : "bg-white border-light"}`}
      >
        <div
          className="flex-grow-1 p-3 pointer"
          style={{ minWidth: 0 }}
          onClick={() => {
            if (active) {
              this.setState({ menu_uid: null });
              return;
            }
            this.guard(() => this.selectRole(role));
          }}
        >
          <div className={`fw-bold text-14 d-flex align-items-center gap-2 ${active ? "text-primary" : "text-dark"}`}>
            <span className="text-break">{role.name}</span>
            {active && this.isDirty() && (
              <span
                className="rounded-circle d-inline-block"
                style={{ width: 8, height: 8, backgroundColor: "#E59A18" }}
                title={t("rp_dirty_summary").replace("{n}", this.getDirtyRows().length)}
              />
            )}
          </div>
          <div className="text-secondary text-12 d-flex align-items-center">
            <span className="icon me-1">{""}</span>
            {formatNumber(role.user_count || 0)} {t("rp_users_unit")} · {code}
          </div>
        </div>
        {showMenu && (
          <div data-role-menu className="pe-2">
            <button
              type="button"
              className="btn btn-sm border-0 text-secondary"
              aria-label={`${t("rp_menu_options")} ${role.name}`}
              aria-expanded={menu_uid === role.uid}
              onClick={() => this.setState({ menu_uid: menu_uid === role.uid ? null : role.uid })}
            >
              <span className="icon">{""}</span>
            </button>
            {menu_uid === role.uid && (
              <div
                className="position-absolute bg-white border rounded-3 shadow p-1 d-flex flex-column"
                style={{ right: 8, top: 48, width: 200, zIndex: 20 }}
                role="menu"
              >
                {can("roles.update") && (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn btn-white text-start text-14 d-flex align-items-center gap-2"
                    onClick={() => this.guard(() => this.selectRole(role, can("roles.update") ? { renaming: true, rename_value: role.name, rename_error: "" } : {}))}
                  >
                    <span className="icon">{""}</span> {t("rp_menu_rename")}
                  </button>
                )}
                {can("roles.create") && (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn btn-white text-start text-14 d-flex align-items-center gap-2"
                    onClick={() => this.guard(() => this.CopyRole(role))}
                  >
                    <span className="icon">{""}</span> {t("rp_menu_copy")}
                  </button>
                )}
                {can("roles.delete") && (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn btn-white text-start text-14 text-danger d-flex align-items-center gap-2"
                    onClick={() => { this.setState({ menu_uid: null }); this.Delete(role.uid); }}
                  >
                    <span className="icon">{""}</span> {t("rp_menu_delete")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  renderTitle = (activeRole) => {
    const { renaming, rename_value, rename_error } = this.state;
    if (renaming) {
      return (
        <div>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              autoFocus
              maxLength={200}
              className={`form-control fw-bold ${rename_error ? "is-invalid" : ""}`}
              style={{ minWidth: 260 }}
              aria-label={t("role_name")}
              value={rename_value}
              onFocus={(e) => e.target.select()}
              onChange={this.onRenameChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  this.CommitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  this.cancelRename();
                }
              }}
            />
            <button type="button" className="btn btn-primary btn-sm" aria-label={t("rp_rename_ok")} onClick={this.CommitRename}>
              <span className="icon">{""}</span>
            </button>
            <button type="button" className="btn btn-white border btn-sm" aria-label={t("rp_rename_cancel")} onClick={this.cancelRename}>
              <span className="icon">{""}</span>
            </button>
          </div>
          {rename_error ? (
            <div className="text-danger text-12 mt-1" role="alert">{rename_error}</div>
          ) : (
            <div className="text-secondary text-12 mt-1">{t("rp_rename_hint")}</div>
          )}
        </div>
      );
    }
    return (
      <div>
        <div className="d-flex align-items-center gap-2">
          <h4 className="fw-bold text-dark mb-0">{activeRole?.name}</h4>
          {can("roles.update") && (
            <button
              type="button"
              className="btn btn-white border btn-sm"
              aria-label={t("rp_rename_tip")}
              title={t("rp_rename_tip")}
              onClick={this.startRename}
            >
              <span className="icon">{""}</span>
            </button>
          )}
        </div>
        <div className="text-secondary text-12 mt-1">
          {formatNumber(activeRole?.user_count || 0)} {t("rp_users_unit")}
        </div>
      </div>
    );
  };

  render() {
    const {
      data,
      active_role_id,
      active_tab,
      modal_create,
      modal_leave,
      role_search,
      toast_message,
    } = this.state;
    const activeRole = data?.find((r) => r.uid === active_role_id);
    const roleQuery = role_search.trim().toLowerCase();
    const roleList = (data || []).filter((r) => !roleQuery || String(r.name).toLowerCase().includes(roleQuery));

    const dirtyRows = activeRole ? this.getDirtyRows() : [];
    const typeChanged = activeRole ? this.isTypeChanged() : false;
    const dirty = dirtyRows.length > 0 || typeChanged;
    const dirtyNames = dirtyRows.slice(0, 4).map((row) => this.rowName(row, Object.keys(row.modulesByGroup)[0] || active_tab)).join(", ");
    const dirtyMore = dirtyRows.length > 4 ? ` ${t("rp_more_modules").replace("{n}", dirtyRows.length - 4)}` : "";

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1 text-dark">
                {t("roles_and_permissions")}
              </h3>
              <div className="text-secondary text-14">
                {t("manage_roles_desc")}
              </div>
            </div>
            {can("roles.create") ? (
            <button
              className="btn btn-primary px-4 fw-bold rounded-3 shadow-sm d-flex align-items-center"
              onClick={() => this.guard(this.openCreate)}
            >
              <span className="me-2 text-16">+</span> {t("add_role")}
            </button>
            ) : null}
          </div>

          <div className="row g-4">
            {/* Left Panel: Roles List */}
            <div className="col-lg-4">
              <div className="bg-white rounded-4 shadow-sm p-4 h-100 border border-light">
                <div className="mb-3">
                  <h6 className="fw-bold text-dark mb-0">{t("roles")}</h6>
                  <div className="text-secondary text-12">
                    {formatNumber(data?.length)} {t("roles_defined")}
                  </div>
                </div>

                <div className="d-flex align-items-center border rounded-3 px-3 bg-white mb-3">
                  <span className="icon text-secondary me-2 text-12">{""}</span>
                  <input
                    type="text"
                    className="form-control border-0 shadow-none px-0"
                    placeholder={t("rp_role_search")}
                    aria-label={t("rp_role_search")}
                    value={role_search}
                    onChange={(e) => this.setState({ role_search: e.target.value, menu_uid: null })}
                  />
                </div>

                <div className="d-flex flex-column gap-2">
                  {roleList.map((role) => this.renderRoleItem(role))}
                  {!roleList.length && (
                    <div className="text-secondary text-13 text-center py-3">
                      {t("rp_role_not_found")} {role_search.trim() ? `“${role_search.trim()}”` : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel: Permissions Config */}
            <div className="col-lg-8">
              {activeRole && (
              <div className="bg-white rounded-4 shadow-sm p-4 h-100 border border-light d-flex flex-column">
                {/* Header (Role Title & Action Buttons) */}
                <div className="d-flex flex-wrap justify-content-between align-items-start mb-4 gap-3">
                  {this.renderTitle(activeRole)}
                  {/* ประเภทผู้ใช้ที่ใช้บทบาทนี้ (master) - ว่าง = ทุกประเภท; ประเภทที่พอร์ทัลใดดูอย่างเดียว แท็บนั้นจะติ๊กได้แค่ "ดู" */}
                  <div className="wpx-300">
                    <label className="form-label text-12 fw-bold text-secondary mb-1">{t("role_user_type")}</label>
                    <Select
                      className="form-control"
                      options={this.userTypeOptions()}
                      value={this.state.user_type || ""}
                      onChange={(e) => this.setState({ user_type: e || "" })}
                      placeholder={t("role_user_type_all")}
                      defaultLabel={t("role_user_type_all")}
                    />
                  </div>
                </div>

                {/* Tabs */}
                <div className="d-flex border-bottom gap-4 mb-3">
                  {TABS.map((tab) => (
                    <div
                      key={tab.id}
                      className={`pb-2 pointer fw-bold d-flex align-items-center ${active_tab === tab.id ? "text-primary" : "text-secondary"}`}
                      style={{
                        borderBottom:
                          active_tab === tab.id ? "3px #1976D2 solid" : "",
                      }}
                      onClick={() => this.setState({ active_tab: tab.id })}
                    >
                      <span className="icon me-2">{tab.icon}</span>{" "}
                      {t(tab.label)}
                    </div>
                  ))}
                </div>

                {/* Permissions Table */}
                <div className="flex-grow-1">
                  {this.renderPermissions(active_tab)}
                </div>

                {/* Bottom Save Bar */}
                <div
                  className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 pt-3 px-3 pb-3 border-top rounded-bottom-3"
                  style={{ backgroundColor: dirty ? "#EAF0FF" : "#F6F7FB", position: "sticky", bottom: 0 }}
                >
                  <div className="d-flex align-items-center gap-3 flex-wrap" style={{ minWidth: 0 }}>
                    {can("roles.delete") ? (
                    <button className="btn btn-danger-light border-danger text-danger px-3 py-1 fw-bold rounded-2 d-flex align-items-center shadow-sm"
                    onClick={() => this.Delete(active_role_id)}>
                      <span className="icon me-2">{""}</span>{" "}
                      {t("delete")}
                    </button>
                    ) : null}
                    <div className="text-13" style={{ minWidth: 0 }}>
                      {dirty ? (
                        <>
                          {dirtyRows.length > 0 && (
                            <div>
                              {t("rp_dirty_summary").replace("{n}", dirtyRows.length)}
                              <span className="d-block text-secondary text-12">{dirtyNames}{dirtyMore}</span>
                            </div>
                          )}
                          {typeChanged && <div>{t("rp_dirty_type")}</div>}
                        </>
                      ) : (
                        <span className="text-secondary">{t("rp_no_change")}</span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex gap-2 ms-auto">
                    {dirty && (
                      <button className="btn btn-white border px-3 py-2 fw-bold rounded-2" onClick={this.revertChanges}>
                        {t("cancel")}
                      </button>
                    )}
                    {can("roles.update") ? (
                    <button className="btn btn-primary px-4 py-2 fw-bold rounded-2 d-flex align-items-center shadow-sm"
                    onClick={() => {this.Update()}}>
                      <span className="icon me-2">{""}</span>{" "}
                      {t("save")}
                    </button>
                    ) : null}
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {toast_message && (
          <div
            role="status"
            aria-live="polite"
            className="position-fixed start-50 translate-middle-x bg-dark text-white px-3 py-2 rounded-3 shadow text-14"
            style={{ bottom: 24, zIndex: 2000 }}
          >
            {toast_message}
          </div>
        )}

        {/* MODAL: UNSAVED CHANGES */}
        <Modal
          size="md"
          show={modal_leave}
          onHide={this.closeLeave}
          centered
          contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
        >
          <Modal.Header className="bg-white border-bottom px-4 py-3" closeButton>
            <h4 className="fw-bold mb-0 text-dark">{t("rp_leave_title")}</h4>
          </Modal.Header>
          <Modal.Body className="p-4 bg-white">
            {t("rp_leave_body").replace("{name}", activeRole?.name || "")}
            {dirtyRows.length > 0 && (
              <div className="text-secondary text-13 mt-2">
                {t("rp_dirty_summary").replace("{n}", dirtyRows.length)}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-end gap-2">
            <button className="btn btn-white border px-3 py-2 text-dark rounded-3 fw-bold" onClick={this.closeLeave}>
              {t("rp_leave_stay")}
            </button>
            <button className="btn btn-white border px-3 py-2 text-dark rounded-3 fw-bold" onClick={this.leaveDiscard}>
              {t("rp_leave_discard")}
            </button>
            {can("roles.update") && (
              <button className="btn btn-primary px-3 py-2 rounded-3 fw-bold text-white" onClick={this.leaveSave}>
                {t("rp_leave_save")}
              </button>
            )}
          </Modal.Footer>
        </Modal>

        {/* MODAL: ADD NEW ROLE */}
        <Modal
          size="md"
          show={modal_create}
          onHide={() => this.setState({ modal_create: false })}
          centered
          contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
        >
          <Modal.Header
            className="bg-white border-bottom px-4 py-3"
            closeButton
          >
            <h4 className="fw-bold mb-0 text-dark">{t("add_new_role")}</h4>
          </Modal.Header>

          <Modal.Body className="p-4 bg-white">
            <div className="d-flex flex-column gap-4">
              <div>
                <label className="form-label text-14 text-secondary fw-bold">
                  {t("role_name")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control py-2 text-dark ${this.state.new_role_error ? "is-invalid" : ""}`}
                  placeholder={t("role_name_placeholder")}
                  value={this.state.new_role_name}
                  onChange={(e) =>
                    this.setState({
                      new_role_name: e.target.value,
                      new_role_error: this.validateRoleName(e.target.value, null),
                    })
                  }
                />
                {this.state.new_role_error && (
                  <div className="text-danger text-12 mt-1" role="alert">{this.state.new_role_error}</div>
                )}
              </div>
              <div>
                <label className="form-label text-14 text-secondary fw-bold">{t("role_user_type")}</label>
                <Select
                  className="form-control"
                  options={this.userTypeOptions()}
                  value={this.state.new_role_type || ""}
                  onChange={(e) => this.setState({ new_role_type: e || "" })}
                  placeholder={t("role_user_type_all")}
                  defaultLabel={t("role_user_type_all")}
                />
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-end gap-2 bg-light">
            <button
              className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold"
              onClick={() => this.setState({ modal_create: false })}
            >
              {t("cancel")}
            </button>
            <button
              className="btn btn-primary px-4 py-2 rounded-3 d-flex align-items-center fw-bold text-white shadow-sm"
              onClick={() => this.Create()}
            >
              <span className="icon me-2">{""}</span> {t("save")}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
